import { InfraredImageWithContext } from "@nova/constants"
import { useAppSelector } from "integrations/redux/hooks"
import { convertHexStringToRGBArray } from "@nova/util-strings"
import {
    useInfraredImageCorners,
    useInfraredImageData,
} from "features/data-management/hotspots/editor/infrared-image/InfraredImage"
import { selectInfraredFlightById } from "features/entity-caching/flights/selectors"
import { BitmapLayer, PolygonLayer } from "@deck.gl/layers"
import {
    selectHotspotPanelIsOpen,
    selectHotspotPanelItems,
    selectHotspotPanelOverlayOptions,
} from "panels/hotspot/selectors"
import { selectInfraredImageById } from "features/entity-caching/images/hotspots/selectors"
import { useInfraredImageBoundingBox } from "../useInfraredImageBoundingBox"
import { Position } from "@deck.gl/core"
import { getGlobalTerrainData } from "features/map/GlobalTerrainManager"

export const useIRImageOverlayLayer = () => {
    const selected = useAppSelector(selectHotspotPanelItems)
    const flight = useAppSelector(selectInfraredFlightById(selected.flightId))
    const currentImageId = removeExtension(selected.imageId ?? "")
    const isPanelOpen = useAppSelector(selectHotspotPanelIsOpen)
    const infraredImage = useAppSelector(selectInfraredImageById(selected?.imageId ?? "")) as
        | InfraredImageWithContext
        | undefined

    const flightHasCorners = flight?.job?.extraFeatures?.includes("hasCorners")

    const options = useAppSelector(selectHotspotPanelOverlayOptions)
    const { data: infraredImageData, loading: isInfraredImageDataLoading } = useInfraredImageData(
        infraredImage?.s3Key,
        !!flight?.demoId
    )

    const { data: backupBoundingBox } = useInfraredImageBoundingBox(infraredImage)

    const { data: boundingBoxMap, isLoading: isBoundingBoxLoading } = useInfraredImageCorners(
        selected.flightId,
        {
            disabled: !flightHasCorners,
        }
    )

    if (!infraredImageData || !backupBoundingBox || isInfraredImageDataLoading) return []

    const rawBoundingBox = boundingBoxMap?.[currentImageId] ?? backupBoundingBox

    const boundingBox = rawBoundingBox?.map((v, i) =>
        v[0] && v[1] ? v : backupBoundingBox?.[i]
    ) as [Position, Position, Position, Position]

    if (
        !isPanelOpen ||
        !infraredImage ||
        !infraredImageData ||
        !infraredImage.yaw ||
        !boundingBox ||
        isBoundingBoxLoading
    )
        return []

    return [
        new BitmapLayer({
            id: `${infraredImage?.id}-bitmap`,
            image: infraredImageData?.imageUrl,
            bounds: boundingBox,
            extensions: [...getGlobalTerrainData().extensions],
            visible: !isInfraredImageDataLoading && options.imageOverlay,
            loadOptions: {
                mimeType: "image/jpeg",
            },
        }),
        new PolygonLayer({
            id: `${infraredImage?.id}-fov`,
            data: [boundingBox],
            visible: options.fieldOfView && !isBoundingBoxLoading,
            getPolygon: (d) => d,
            filled: false,
            stroked: true,
            getLineColor: convertHexStringToRGBArray("#27BBFF"),
            getLineWidth: 2,
            lineWidthUnits: "pixels",
            lineWidthScale: 1,
            lineWidthMinPixels: 1,
            lineWidthMaxPixels: 10,
            extensions: [...getGlobalTerrainData().extensions],
        }),
    ]
}

function removeExtension(filename: string) {
    return filename.replace(/\.[^/.]+$/, "")
}
